import { useState } from 'react';
import { useLocation } from 'wouter';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useWorkOrderCreate, WORK_ORDER_PRIORITY_LABELS, WORK_ORDER_TYPE_LABELS } from '@/hooks/useSwo';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { SWO_PERMISSIONS, hasPermission } from '@/lib/swoPermissions';
import {
  AssociationLookupSelector,
  type SelectedAssociation,
} from '@/components/business/AssociationLookupSelector';

const WORK_ORDER_TYPES = ['follow_up', 'health_assessment', 'infusion_prep', 'document_collection', 'other'];
const WORK_ORDER_PRIORITIES = ['low', 'normal', 'high', 'urgent'];

export default function WorkOrderCreatePage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { loading, error, create } = useWorkOrderCreate();

  const [customer, setCustomer] = useState<SelectedAssociation | null>(null);
  const [appointmentId, setAppointmentId] = useState('');
  const [type, setType] = useState('follow_up');
  const [priority, setPriority] = useState('normal');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignee, setAssignee] = useState<SelectedAssociation | null>(null);
  const [dueAt, setDueAt] = useState('');

  const canCreate = hasPermission(permissions, SWO_PERMISSIONS.WORK_ORDER_CREATE);

  if (!canCreate) {
    return <AccessDenied resource="服务工单" />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!customer?.id || !title.trim()) {
      toast.error('请选择客户并填写标题');
      return;
    }
    const result = await create({
      customerId: customer.id,
      appointmentId: appointmentId.trim() || undefined,
      type,
      priority,
      title: title.trim(),
      description: description.trim() || undefined,
      assigneeId: assignee?.id ?? undefined,
      dueAt: dueAt || undefined,
    });
    if (result) {
      toast.success('工单已创建');
      navigate(`/operations/work-orders/${result.id}`);
    } else if (error) {
      toast.error(`创建失败：${error.message}`);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <a href="/operations/work-orders" className="hover:underline">
          服务工单
        </a>
        <span>/</span>
        <span className="text-foreground">新建工单</span>
      </div>

      <h2 className="text-xl font-semibold">新建服务工单</h2>

      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="customerId">
                客户 <span className="text-red-500">*</span>
              </Label>
              <AssociationLookupSelector
                id="customerId"
                kind="customer"
                value={customer}
                onChange={setCustomer}
                placeholder="按姓名搜索客户…"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="appointmentId">预约 ID（可选）</Label>
              <Input
                id="appointmentId"
                value={appointmentId}
                onChange={(e) => setAppointmentId(e.target.value)}
                placeholder="请输入预约 ID"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="title">
              标题 <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="请输入工单标题"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="type">工单类型</Label>
              <select
                id="type"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                {WORK_ORDER_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {WORK_ORDER_TYPE_LABELS[t] ?? t}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="priority">优先级</Label>
              <select
                id="priority"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                {WORK_ORDER_PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {WORK_ORDER_PRIORITY_LABELS[p] ?? p}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">描述（可选）</Label>
            <textarea
              id="description"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="请输入工单描述…"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="assigneeId">负责人（可选）</Label>
              {/*
                service_work_orders.assignee_id is FK to users (see
                prisma/schema.prisma). Use kind="user" so the form submits
                a real users.id and the backend's user-scope filters /
                reassignment paths keep working. Issue #344 P1 fix.
              */}
              <AssociationLookupSelector
                id="assigneeId"
                kind="user"
                value={assignee}
                onChange={setAssignee}
                placeholder="搜索负责人姓名…"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dueAt">截止日期（可选）</Label>
              <Input
                id="dueAt"
                type="date"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={loading}>
              {loading ? '创建中…' : '创建工单'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/operations/work-orders')}
            >
              取消
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
